// src/components/crm/QuoteDeleteControls.js — estdel-f9ac8ba8
//
// Delete (hide) / Restore an estimate. Nothing is ever erased (CRA six-year rule): the
// setQuoteArchived Cloud Function marks it deleted, and it disappears from every list, count,
// report and the customer portal until an Owner or Admin restores it. The server is the real
// gate (who, and which estimates); canDeleteQuote / canRestoreQuote only decide what is OFFERED,
// so nobody meets a refusal for a button they were shown.
//
//   <DeleteQuoteButton>     red-outlined "Delete" + the confirm box
//   <QuoteDeletedBanner>    grey "Deleted by … on …" bar, with Restore for Owner / Admin
//   <QuoteRestoredNotice>   the green "restored" line shown after a Restore
//
// Per-document wording: resolveDocTerminology(quote.docType) (terminology freeze rule).
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Trash2, RotateCcw, CheckCircle2, EyeOff, Loader2 } from 'lucide-react';
import { functions } from '../../firebase/config';
import ConfirmationModal from '../common/ConfirmationModal';
import { formatDate, toJsDate } from '../../utils/formatters';
import { resolveDocTerminology, canDeleteQuote, canRestoreQuote, isQuoteArchived, normalizeStatus, QUOTE_STATUSES } from '../../utils/quoteSchema';

// The one call. archived: true = delete, false = restore. Resolves to the server's answer.
export const setQuoteArchived = (quoteId, archived) =>
    httpsCallable(functions, 'setQuoteArchived')({ quoteId, archived }).then((r) => r.data);

// The server's own sentence (it is written for people), else a plain fallback.
export const archiveErrorMessage = (err, restoring) =>
    (err && typeof err.message === 'string' && err.message && !/^internal$/i.test(err.message) ? err.message : null)
    || (restoring ? 'Could not restore it. Please try again.' : 'Could not delete it. Please try again.');

const wordOf = (quote) => resolveDocTerminology(quote?.docType).word;

export const DeleteQuoteButton = ({ quote, currentUserProfile, className = '' }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (!quote?.id || isQuoteArchived(quote) || !canDeleteQuote(quote, currentUserProfile?.role, currentUserProfile?.id)) return null;
    const word = wordOf(quote).toLowerCase();
    const number = quote.quoteNumber ? `${quote.quoteNumber} ` : '';
    const customer = quote.customerName || quote.customerDetails?.name || 'this customer';

    const confirm = async () => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            await setQuoteArchived(quote.id, true);
            setOpen(false); // the live estimate now carries archived: true — the page shows the banner
        } catch (err) {
            setError(archiveErrorMessage(err, false));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => { setError(''); setOpen(true); }}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-md border-2 border-red-600 bg-white text-red-600 font-semibold hover:bg-red-50 ${className}`}
            >
                <Trash2 size={16} aria-hidden="true" /> Delete
            </button>
            <ConfirmationModal
                isOpen={open}
                onClose={() => { if (!busy) setOpen(false); }}
                onConfirm={confirm}
                title={`Delete ${word} ${number}for ${customer}?`}
                message="It will be hidden from lists, reports and the customer portal. An Owner or Admin can restore it. The record is kept."
                confirmText={`Delete ${word}`}
            >
                {busy && <p className="-mt-3 mb-4 flex items-center gap-2 text-sm text-gray-500"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Deleting…</p>}
                {error && <p role="alert" className="-mt-3 mb-4 text-sm text-red-600">{error}</p>}
            </ConfirmationModal>
        </>
    );
};

export const QuoteDeletedBanner = ({ quote, currentUserProfile, onRestored }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (!isQuoteArchived(quote)) return null;
    const by = quote.archivedBy?.name;
    const on = quote.archivedAt ? formatDate(toJsDate(quote.archivedAt)) : null;
    const lead = by && on ? `Deleted by ${by} on ${on}.` : by ? `Deleted by ${by}.` : on ? `Deleted on ${on}.` : 'Deleted.';
    const canRestore = canRestoreQuote(quote, currentUserProfile?.role);

    const restore = async () => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            await setQuoteArchived(quote.id, false);
            if (onRestored) onRestored(quote);
        } catch (err) {
            setError(archiveErrorMessage(err, true));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div role="status" className="max-w-4xl mx-auto mb-4 rounded-lg border border-gray-300 bg-gray-100 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <EyeOff size={18} className="text-gray-500 shrink-0" aria-hidden="true" />
                <p className="flex-1 text-sm text-gray-800"><span className="font-semibold">{lead}</span> Hidden from lists, reports and the customer portal.</p>
                {canRestore && (
                    <button type="button" onClick={restore} disabled={busy}
                        className="flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-800 font-semibold hover:bg-gray-50 disabled:opacity-60">
                        {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <RotateCcw size={16} aria-hidden="true" />}
                        {busy ? 'Restoring…' : 'Restore'}
                    </button>
                )}
            </div>
            {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export const QuoteRestoredNotice = ({ quote, onDismiss }) => (
    <div role="status" className="max-w-4xl mx-auto mb-4 rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3">
        <CheckCircle2 size={18} className="text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="flex-1 text-sm text-green-800">
            <span className="font-semibold">{wordOf(quote)}{quote?.quoteNumber ? ` ${quote.quoteNumber}` : ''} restored.</span>{' '}
            {normalizeStatus(quote?.status) === QUOTE_STATUSES.DRAFT
                ? 'It’s back in lists and reports.' // a Draft is never in the customer portal
                : 'It’s back in lists, reports and the customer portal.'}
        </p>
        {onDismiss && <button type="button" onClick={onDismiss} className="text-sm font-semibold text-green-800 hover:underline">Dismiss</button>}
    </div>
);
